import './index.css'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, persistor, RootState } from '@/app-store/store.ts'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { AppRoutePaths } from './routing/route_paths.ts'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@/common/components/Menu.tsx'
import { DesktopSidebar, MobileSidebar } from '@/layouts/Sidebars.tsx'
import ProgramSelect from '@/common/components/ProgramSelect.tsx'
import { PageHeaderProvider } from '@/layouts/PageHeaderProvider.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { ProfileCircleIcon } from '@/common/icons/general.tsx'
import { DownChevronIcon } from '@/common/icons/arrows.tsx'
import { IconType } from '@/common/icons/types.ts'
import { cn } from '@/common/utilities.ts'
import { useLayout } from '@/layouts/LayoutProvider.tsx'
import { useNotification } from './app/common/components/Notifications/useNotification.tsx'
import { TimeoutModal } from '@/pages/timeout/TimeoutModal.tsx'
import LoadImage from '@/common/components/LoadImage.tsx'
import { ENV } from '@/common/constants.ts'
import { resetSelectedProgramAndYear } from '@/app-store/programs/slice.ts'
import AceLogo from '@assets/ace-pencil.png'
import { AuthService } from './app/pages/authentication/AuthService'

function App() {
  const auth = useSelector((state: RootState) => state.auth)
  const {
    activeSidebarOpen,
    setMobileNavOpen,
    onDesktop /* setDesktopNavOpen */,
  } = useLayout()
  const [showNotification, setShowNotification] = useState(false)
  const [imageVisible, setImageVisible] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const { addNotification } = useNotification()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)

  /* const handleProgramChange = (program: GlobalProgram | null) => {
    setSelectedProgram(null)
    setDesktopNavOpen(true)
    navigate(AppRoutePaths.programs.index)
  } */

  const handleMobileSidebar = () => {
    setMobileNavOpen(selectedProgramYear !== null)
    if (selectedProgramYear === null) {
      addNotification('Please select a program')
    }
  }

  const handleLogOut = async () => {
    dispatch(resetSelectedProgramAndYear())
    AuthService.logout()
    //FALTA PURGAR EL ESTADO:
    await persistor.purge()
    await persistor.purge()
  }

  const gotoMyProfile = () => {
    if (selectedProgramYear === null) {
      addNotification('Please select a program')
      return
    }
    navigate(AppRoutePaths.my_profile.index)
  }

  useEffect(() => {
    if (showNotification && selectedProgramYear === null) {
      addNotification('Please select a program')
      setShowNotification(false) // Reset the state after triggering the notification
    }
  }, [showNotification])

  // useEffect(() => {
  //   if (auth.isAuthenticated && !selectedProgramYear) {
  //     navigate(AppRoutePaths.programs.index)
  //   }
  // }, [])

  useEffect(() => {
    if (auth.user?.profile_picture) {
      setImageVisible(true)
    } else {
      setImageVisible(false)
    }
  }, [auth.user?.profile_picture])

  const encodedQS = params.get('q')

  if (encodedQS) {
    try {
      const decodedQS = atob(encodedQS)
      const decodedParams = new URLSearchParams(decodedQS)
      const dict: Record<string, string> = {}

      for (const [key, value] of decodedParams.entries()) {
        dict[key] = value
      }

      if (dict['user_id'] && dict['email'] && dict['token']) {
        handleLogOut()
        return (
          <Navigate
            to={AppRoutePaths.auth.sign_up}
            state={{
              user_id: dict['user_id'],
              email: dict['email'],
              token: dict['token'],
            }}
          />
        )
      }
    } catch (e) {
      /* empty */
    }
  }

  if (!auth.isAuthenticated) {
    return <Navigate to={AppRoutePaths.auth.login} />
  } else if (location.pathname === '/') {
    return <Navigate to={AppRoutePaths.dashboard.index} />
  }

  return (
    <>
      <div className="h-full flex flex-col">
        <TimeoutModal />
        {onDesktop ? (
          <DesktopSidebar showNotification={setShowNotification} />
        ) : (
          <MobileSidebar />
        )}
        <div
          className={cn('flex-grow flex flex-col', {
            'lg:pl-72': activeSidebarOpen,
            'lg:pl-[76px]': !activeSidebarOpen,
          })}
        >
          <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={handleMobileSidebar}
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <span className="h-6 w-6">
                <i className="ki-filled ki-burger-menu-5"></i>
              </span>
            </button>
            <ProgramSelect />
            {/* <AdvancedSelect<SchoolYearOption>
              options={SchoolYearOptions}
              displayValue={(option) => option?.school_year ?? ''}
              onChange={setSelectedSchoolYear}
              placeholder="Search School Years"
              embeddedIcon={<ChevronDownIcon className="text-lg" />}
              defaultOption={SchoolYearOptions[0]}
            /> */}
            <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* Profile dropdown */}
                <Menu>
                  <MenuButton className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <div className="w-10 h-10 bg-primary-100 flex items-center justify-center rounded-full">
                      <LoadImage
                        imageUrl={
                          imageVisible
                            ? `${ENV.VITE_API_URL}/admin/download-file?path=${auth.user?.profile_picture}`
                            : ''
                        }
                        firstName={auth.user?.first_name}
                        lastName={auth.user?.last_name}
                      />
                    </div>
                    <span className="hidden lg:flex lg:items-center">
                      <span className="ml-2 h-5 w-5 text-gray-400">
                        <DownChevronIcon />
                      </span>
                    </span>
                  </MenuButton>
                  <MenuItems className="w-60 divide-y divide-gray-100">
                    <div className="px-4 py-3">
                      <p className="truncate text-sm text-gray-900">
                        {auth?.user?.first_name} {auth?.user?.last_name}
                      </p>
                      <p className="font-normal text-sm text-gray-600">
                        {auth?.user?.email ?? ''}
                      </p>
                    </div>

                    <div className="py-1">
                      <MenuItem
                        className="group pl-4 flex justify-start items-center gap-3 text-sm font-normal text-gray-700"
                        onClick={gotoMyProfile}
                      >
                        <ProfileCircleIcon
                          className="text-lg text-gray-500 group-hover:text-primary"
                          type={IconType.Outline}
                        />
                        <span>My Profile</span>
                      </MenuItem>
                    </div>

                    <div className="mb-3 px-2 pt-3">
                      <Button
                        type="submit"
                        variant="outline"
                        size="xs"
                        className="w-full py-2"
                        onClick={handleLogOut}
                      >
                        Log Out
                      </Button>
                    </div>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </header>

          <main className="flex-grow bg-muted-white ">
            <div
              className={cn('mx-2 px-4 py-8 flex flex-col h-full', {
                'sm:px-6 sm:mx-18 lg:px-10 lg:mx-24 xl:mx-10':
                  location.pathname != '/applicants/pre_selected' &&
                  location.pathname != '/appeals' &&
                  location.pathname != '/reimbursements' &&
                  location.pathname != '/scholarships/active_scholars' &&
                  !location.pathname.match(/^\/appeals\/[^/]+\/details$/) &&
                  !location.pathname.match(/^\/scholarships\/[^/]+\//),
              })}
            >
              <PageHeaderProvider>
                <Outlet />
              </PageHeaderProvider>
            </div>
          </main>

          <footer className="bg-muted-white px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex text-sm gap-2">
              <div className="flex items-center gap-2 text-sm">
                <img
                  alt="Ace Logo"
                  width="6"
                  src={AceLogo}
                  className="h-auto mb-1"
                />
                @ {new Date().getFullYear()} ACE Scholarships.
              </div>
              <div className="text-gray-400 mt-0.5">
                Powered by SoftPoint LLC
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  )
}

export default App
